import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { externalSystemService } from '../../services/externalSystemService';
import { useNotificationStore } from '../../store/notificationStore';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const RISK_LEVELS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Critical', label: 'Critical' },
];

interface ApiMethodFormProps {
  systemId: string;
  methodId?: string;
  onSuccess?: () => void;
}

/**
 * API method form component for creating and editing API methods.
 */
export const ApiMethodForm = ({ systemId, methodId, onSuccess }: ApiMethodFormProps) => {
  const navigate = useNavigate();
  const isEdit = !!methodId;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [httpMethod, setHttpMethod] = useState('GET');
  const [endpointPath, setEndpointPath] = useState('');
  const [inputSchemaJson, setInputSchemaJson] = useState('{}');
  const [outputSchemaJson, setOutputSchemaJson] = useState('');
  const [toolName, setToolName] = useState('');
  const [toolDescription, setToolDescription] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [riskLevel, setRiskLevel] = useState('Low');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && methodId) {
      loadMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodId, isEdit]);

  const loadMethod = async () => {
    if (!methodId) return;
    setLoading(true);
    setError(null);
    try {
      const methods = await externalSystemService.getApiMethods(systemId);
      const method = methods.find((m) => m.id === methodId);
      if (method) {
        setName(method.name);
        setDescription(method.description || '');
        setHttpMethod(method.httpMethod);
        setEndpointPath(method.endpointPath);
        setToolName(method.toolName);
        setToolDescription(method.toolDescription);
        setRequiresApproval(method.requiresApproval);
        setRiskLevel(method.riskLevel);
        setIsActive(method.isActive);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load API method';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!endpointPath.trim()) {
      setError('Endpoint path is required');
      return;
    }

    if (!toolName.trim()) {
      setError('Tool name is required');
      return;
    }

    // Validate JSON fields if provided
    if (inputSchemaJson.trim()) {
      try {
        JSON.parse(inputSchemaJson.trim());
      } catch {
        setError('Input schema JSON must be valid JSON');
        return;
      }
    }

    if (outputSchemaJson.trim()) {
      try {
        JSON.parse(outputSchemaJson.trim());
      } catch {
        setError('Output schema JSON must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        httpMethod,
        endpointPath: endpointPath.trim(),
        inputSchemaJson: inputSchemaJson.trim() || '{}',
        outputSchemaJson: outputSchemaJson.trim() || undefined,
        toolName: toolName.trim(),
        toolDescription: toolDescription.trim() || '',
        requiresApproval,
        riskLevel,
        isActive,
      };

      if (isEdit && methodId) {
        await externalSystemService.updateApiMethod(systemId, methodId, data);
        addNotification('API method updated successfully', 'success');
      } else {
        await externalSystemService.createApiMethod(systemId, data);
        addNotification('API method created successfully', 'success');
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/external-systems/${systemId}`);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save API method';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/external-systems/${systemId}`)}>
          Back
        </Button>
        <Typography variant="h5">{isEdit ? 'Edit API Method' : 'Create API Method'}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>HTTP Method</InputLabel>
              <Select value={httpMethod} onChange={(e) => setHttpMethod(e.target.value)} label="HTTP Method">
                {HTTP_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Endpoint Path"
              fullWidth
              required
              value={endpointPath}
              onChange={(e) => setEndpointPath(e.target.value)}
              helperText="Path relative to base URL (e.g., /api/v1/users or /api/v1/users/{id})"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Tool Name"
              fullWidth
              required
              value={toolName}
              onChange={(e) => setToolName(e.target.value)}
              helperText="Unique name for the tool (used by agents)"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Risk Level</InputLabel>
              <Select value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)} label="Risk Level">
                {RISK_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Tool Description"
              fullWidth
              required
              multiline
              rows={2}
              value={toolDescription}
              onChange={(e) => setToolDescription(e.target.value)}
              helperText="Description of what this tool does (used by agents)"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Input Schema JSON"
              fullWidth
              multiline
              rows={6}
              value={inputSchemaJson}
              onChange={(e) => setInputSchemaJson(e.target.value)}
              helperText="JSON schema for input parameters (OpenAPI/JSON Schema format)"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Output Schema JSON"
              fullWidth
              multiline
              rows={4}
              value={outputSchemaJson}
              onChange={(e) => setOutputSchemaJson(e.target.value)}
              helperText="JSON schema for output response (optional)"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />
              }
              label="Requires Approval"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Active"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate(`/external-systems/${systemId}`)}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};
